import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, Save, Printer, User, Users,
  School, MapPin, Award, Plus, Trash2, GraduationCap,
  Phone, Home, FileText, AlertCircle, UserCheck, Loader2,
  ArrowRight, Activity, UserMinus, UserCircle, Briefcase,
  Mail, Hash, MapPinHouse, Building2, Landmark,
  Percent, Trophy, CircleCheck, CircleX, Info, Keyboard,
  ScanLine, Upload, X, CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import { api, API_URL } from '../../lib/api';
import { Label } from '../../components/ui/label';
import loadImage from 'blueimp-load-image';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { DatePicker } from '../../components/ui/date-picker';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { IconInput, IconSelect, IconTextarea } from '../../components/ui/form-inputs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ocrService } from '../../services/ocr';
import { aiService, type ParsedStudentData } from '../../services/ai';
import { useClasses, useSections } from '../../hooks/useClasses';

interface FormData {
  studentName: string;
  gender: string;
  dateOfBirth: string;
  classId: string;
  sectionId: string;
  batchId: string;
  rollNo: string;
  admissionDate: string;
  status: 'active' | 'drop' | 'enquiry';
  schoolName: string;
  schoolBoard: string;
  previousClassBoard: string;
  previousMarks: string;
  previousPercentage: string;
  scholarshipApplicable: boolean;
  fatherName: string;
  fatherQualification: string;
  fatherOccupation: string;
  motherName: string;
  motherQualification: string;
  motherOccupation: string;
  fatherPhone1: string;
  fatherPhone2: string;
  motherPhone1: string;
  motherPhone2: string;
  whatsappNumber: string;
  preferredMessagingNumber: string;
  houseNo: string;
  villageTown: string;
  postOffice: string;
  district: string;
  fullAddress: string;
  siblings: Array<{ name: string; className: string; school: string }>;
  contacts: Array<{ label: string; phone: string; altPhone: string }>;
}

const emptyFormData: FormData = {
  studentName: '', gender: '', dateOfBirth: '', classId: '', sectionId: '',
  batchId: '', rollNo: '', admissionDate: new Date().toISOString().split('T')[0],
  status: 'active', schoolName: '', schoolBoard: 'CBSE', previousClassBoard: '',
  previousMarks: '', previousPercentage: '', scholarshipApplicable: false,
  fatherName: '', fatherQualification: '', fatherOccupation: '',
  motherName: '', motherQualification: '', motherOccupation: '',
  fatherPhone1: '', fatherPhone2: '', motherPhone1: '', motherPhone2: '',
  whatsappNumber: '', preferredMessagingNumber: '',
  houseNo: '', villageTown: '', postOffice: '', district: '', fullAddress: '',
  siblings: [],
  contacts: [],
};

const statusOptions = [
  { value: 'active' as const, label: 'Active', icon: Activity, color: 'bg-lime-500', borderColor: 'border-lime-500', textColor: 'text-lime-600', bgColor: 'bg-lime-50 dark:bg-lime-500/10' },
  { value: 'drop' as const, label: 'Dropped', icon: UserMinus, color: 'bg-rose-500', borderColor: 'border-rose-500', textColor: 'text-rose-600', bgColor: 'bg-rose-50 dark:bg-rose-500/10' },
  { value: 'enquiry' as const, label: 'Enquiry', icon: UserCircle, color: 'bg-amber-500', borderColor: 'border-amber-500', textColor: 'text-amber-600', bgColor: 'bg-amber-50 dark:bg-amber-500/10' },
];

const fieldTooltips = {
  studentName: 'Enter student\'s full name as per records',
  gender: 'Select student\'s gender',
  dateOfBirth: 'Student\'s date of birth',
  admissionDate: 'Date of admission to the institute',
  classId: 'Current class enrolled',
  sectionId: 'Batch (A/B/C...)',
  rollNo: 'Unique roll number in class',
  schoolName: 'Previous school name',
  schoolBoard: 'Education board (CBSE, ICSE, State, etc.)',
  previousPercentage: 'Previous class percentage (0-100)',
  status: 'Current student status',
  scholarship: 'Apply if student is eligible for scholarship',
  fatherName: 'Father\'s full name',
  fatherOccupation: 'Father\'s occupation/profession',
  motherName: 'Mother\'s full name',
  motherOccupation: 'Mother\'s occupation/profession',
  fatherPhone1: 'Primary contact number (with country code)',
  whatsappNumber: 'WhatsApp number for notifications',
  houseNo: 'House/Building number',
  villageTown: 'Village or town name',
  postOffice: 'Post office name',
  district: 'District name',
  fullAddress: 'Complete postal address',
};

function FieldLabel({ label, required, tooltip }: { label: string; required?: boolean; tooltip?: string }) {
  return (
    <div className="flex items-center gap-2">
      <Label className="text-sm font-medium">{label}</Label>
      {required && <span className="text-red-500">*</span>}
      {tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="w-3.5 h-3.5 text-light-text-muted cursor-help" />
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

export default function AddStudentPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const editId = params.id || params.studentId; // Support both /students/:id/edit and /modules/.../students/:studentId/edit
  const { module: moduleParam, classId: routeClassId, batchId: routeBatchId } = params;
  const isModuleEdit = !!moduleParam; // If module param exists, we're in module flow
  const isEnquiry = searchParams.get('enquiry') === 'true';

  const isEdit = !!editId;
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const queryClassId = searchParams.get('classId') || '';
  const queryBatchId = searchParams.get('batchId') || '';
  const [formData, setFormData] = useState<FormData>({
    ...emptyFormData,
    ...(isEnquiry ? { status: 'enquiry' as const } : {}),
    ...(queryClassId && !isEdit ? { classId: queryClassId } : {}),
    ...(routeClassId && !isEdit ? { classId: routeClassId } : {}),
    ...(queryBatchId && !isEdit ? { batchId: queryBatchId } : {}),
    ...(routeBatchId && !isEdit ? { batchId: routeBatchId } : {}),
  });
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const { data: classes, isLoading: classesLoading } = useClasses();
  const { data: sections, isLoading: sectionsLoading } = useSections(formData.classId);
  const { data: batches, isLoading: batchesLoading } = useQuery({
    queryKey: ['batches'],
    queryFn: async () => {
      const response = await api.get('/api/batches');
      return response.data as Array<{ id: number | string; name: string }>;
    },
  });
  const [isOCRLoading, setIsOCRLoading] = useState(false);
  const [ocrPreview, setOcrPreview] = useState<{ show: boolean; data: any; parsedText: string }>({ show: false, data: null, parsedText: '' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [savingPhoto, setSavingPhoto] = useState<boolean>(false);
  const [savedFormData, setSavedFormData] = useState<FormData | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'sibling' | 'contact'; index: number } | null>(null);

  const { data: existingStudent, isLoading: isLoadingStudent } = useQuery({
    queryKey: ['student-edit', editId],
    queryFn: async () => {
      if (!editId) return null;
      const response = await api.get<any>(`/api/students/${editId}`);
      return response.data;
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (!isEdit && !isLoadingStudent && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isEdit, isLoadingStudent]);

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  useEffect(() => {
    if (isEdit && existingStudent) {
      const s = existingStudent;
      const mappedStatus = ['active', 'drop', 'left', 'enquiry'].includes(s.status) ? (s.status === 'left' ? 'drop' : s.status) : 'active';
      const mapped: FormData = {
        studentName: s.student_name || '',
        gender: s.gender || '',
        dateOfBirth: s.date_of_birth || '',
        classId: s.class_id ? String(s.class_id) : '',
        sectionId: s.section_id ? String(s.section_id) : '',
        batchId: s.batch_id ? String(s.batch_id) : (s.section_id ? String(s.section_id) : ''),
        rollNo: s.roll_no || '',
        admissionDate: s.admission_date || new Date().toISOString().split('T')[0],
        status: mappedStatus,
        schoolName: s.school_name || '',
        schoolBoard: s.school_board || '',
        previousClassBoard: s.previous_class_board || '',
        previousMarks: s.previous_marks || '',
        previousPercentage: s.previous_percentage ? String(s.previous_percentage) : '',
        scholarshipApplicable: !!s.scholarship_applicable,
        fatherName: s.parents?.father_name || '',
        fatherQualification: s.parents?.father_qualification || '',
        fatherOccupation: s.parents?.father_occupation || '',
        motherName: s.parents?.mother_name || '',
        motherQualification: s.parents?.mother_qualification || '',
        motherOccupation: s.parents?.mother_occupation || '',
        fatherPhone1: s.parents?.father_phone_1 || '',
        fatherPhone2: s.parents?.father_phone_2 || '',
        motherPhone1: s.parents?.mother_phone_1 || '',
        motherPhone2: s.parents?.mother_phone_2 || '',
        whatsappNumber: s.parents?.whatsapp_number || '',
        preferredMessagingNumber: s.parents?.preferred_messaging_number || '',
        houseNo: s.address?.house_no || '',
        villageTown: s.address?.village_town || '',
        postOffice: s.address?.post_office || '',
        district: s.address?.district || '',
        fullAddress: s.address?.full_address || '',
        siblings: s.siblings?.map((sib: any) => ({
          name: sib.name || '',
          className: sib.class_name || '',
          school: sib.school || '',
        })) || [],
        contacts: [],
      };
      // Build phone contacts from existing parent data
      const c: Array<{ label: string; phone: string; altPhone: string }> = [];
      if (s.parents?.father_phone_1) c.push({ label: 'Father', phone: s.parents.father_phone_1, altPhone: s.parents.father_phone_2 || '' });
      if (s.parents?.mother_phone_1) c.push({ label: 'Mother', phone: s.parents.mother_phone_1, altPhone: s.parents.mother_phone_2 || '' });
      if (s.parents?.whatsapp_number) {
        const isDiff = s.parents.whatsapp_number !== s.parents?.father_phone_1 && s.parents.whatsapp_number !== s.parents?.mother_phone_1;
        if (isDiff) c.push({ label: 'WhatsApp', phone: s.parents.whatsapp_number, altPhone: '' });
      }
      mapped.contacts = c;
      setFormData(mapped);
      setSavedFormData(JSON.parse(JSON.stringify(mapped)));

      // Load existing photo - find one with a valid public_url
      const photoDoc = s.documents?.find((d: any) => d.document_type === 'student_photo' && d.public_url);
      if (photoDoc?.public_url) {
        const fullUrl = photoDoc.public_url.startsWith('http') ? photoDoc.public_url : `${API_URL}${photoDoc.public_url}`;
        setPhotoPreview(fullUrl);
      }
    }
  }, [isEdit, existingStudent]);

  const saveMutation = useMutation({
    mutationFn: async (payload: any) => {
      if (isEdit && editId) {
        return api.patch<any>(`/api/students/${editId}`, payload);
      }
      return api.post<any>('/api/students', payload);
    },
    onSuccess: async (response) => {
      toast.success(isEdit ? 'Student updated successfully' : 'Student created successfully');
      // Invalidate ALL student-related queries to ensure fresh data everywhere
      queryClient.invalidateQueries({ predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && (
          key === 'students' || key === 'module-students' || key === 'student' ||
          key === 'student-edit' || key === 'module-student-detail' ||
          key === 'dashboard-summary' || key === 'module-batches' ||
          key === 'student-attendance' || key === 'student-fees'
        );
      }});
      if (isEdit && editId) {
        queryClient.invalidateQueries({ queryKey: ['student', editId] });
        queryClient.invalidateQueries({ queryKey: ['student-edit', editId] });
        queryClient.invalidateQueries({ queryKey: ['module-student-detail', editId] });
      }

      // Upload photo if selected
      if (photoFile) {
        const targetStudentId = isEdit ? editId : response?.data?.id;
        if (targetStudentId) {
          const fd = new FormData();
          fd.append('file', photoFile);
          fd.append('documentType', 'student_photo');
          fd.append('studentId', String(targetStudentId));
          try {
            await api.upload('/api/documents/upload', fd);
            toast.success('Photo updated');
            queryClient.invalidateQueries({ queryKey: ['student-edit', editId] });
            queryClient.invalidateQueries({ queryKey: ['student', editId] });
          } catch (err: any) {
            toast.error(err.message || 'Failed to upload photo');
          }
        }
      }

      // Redirect logic:
      // 1. If editing from student profile (has searchParam module), go back to profile
      // 2. If in module flow (route params have module), go back to module student list
      // 3. Otherwise go to /modules/students/classes
      if (isEdit) {
        const profileModule = searchParams.get('module');
        const profileClassId = searchParams.get('classId');
        const profileBatchId = searchParams.get('batchId');
        if (profileModule && profileClassId) {
          navigate(`/students/${editId}?module=${profileModule}&classId=${profileClassId}&batchId=${profileBatchId || ''}`);
        } else if (isModuleEdit && moduleParam && routeClassId) {
          navigate(`/modules/${moduleParam}/classes/${routeClassId}/batches/${routeBatchId || formData.batchId}`);
        } else {
          navigate(isEnquiry ? `/students/${response?.data?.id || editId}?enquiry=true` : `/students/${response?.data?.id || editId}`);
        }
      } else {
        if (isEnquiry) {
          navigate(-1);
        } else if (isModuleEdit && moduleParam && routeClassId) {
          navigate(`/modules/${moduleParam}/classes/${routeClassId}/batches/${routeBatchId || formData.batchId}`);
        } else {
          navigate(`/students/${response?.data?.id}`);
        }
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save student');
    },
  });

  const processImageFile = (file: File, done: (blob: Blob) => void) => {
    loadImage(
      file,
      (img: any) => {
        (img as HTMLCanvasElement).toBlob((blob) => {
          if (blob) done(blob);
        }, 'image/jpeg', 0.6);
      },
      { orientation: true, canvas: true, maxWidth: 800, maxHeight: 800 }
    );
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    processImageFile(file, (blob) => {
      const correctedFile = new File([blob], file.name || 'photo.jpg', { type: 'image/jpeg' });
      setPhotoFile(correctedFile);
      setPhotoPreview(URL.createObjectURL(blob));
    });
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
    }
    if (photoInputRef.current) photoInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const updateField = (field: keyof FormData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isDirty = useMemo(() => {
    if (!isEdit || !savedFormData) return true;
    return JSON.stringify(formData) !== JSON.stringify(savedFormData);
  }, [formData, savedFormData, isEdit]);

  const studentInitial = formData.studentName ? formData.studentName.trim().charAt(0).toUpperCase() : '';

  const addSibling = () => {
    setFormData(prev => ({ ...prev, siblings: [...prev.siblings, { name: '', className: '', school: '' }] }));
  };

  const updateSibling = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      siblings: prev.siblings.map((s, i) => i === index ? { ...s, [field]: value } : s),
    }));
  };

  const removeSibling = (index: number) => {
    setFormData(prev => ({ ...prev, siblings: prev.siblings.filter((_, i) => i !== index) }));
  };

  const addContact = () => {
    setFormData(prev => ({ ...prev, contacts: [...prev.contacts, { label: 'Father', phone: '', altPhone: '' }] }));
  };

  const updateContact = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.map((c, i) => i === index ? { ...c, [field]: value } : c),
    }));
  };

  const removeContact = (index: number) => {
    setFormData(prev => ({ ...prev, contacts: prev.contacts.filter((_, i) => i !== index) }));
  };

  const fillFormFromParsed = (parsed: ParsedStudentData) => {
    const classOption = classes?.find((c: any) => c.label === parsed.className);
    const sectionOption = sections?.find((s: any) => s.name === parsed.sectionName);

    setFormData(prev => ({
      ...prev,
      studentName: parsed.studentName || prev.studentName,
      gender: parsed.gender || prev.gender,
      dateOfBirth: parsed.dateOfBirth || prev.dateOfBirth,
      classId: classOption?.value || prev.classId,
      sectionId: sectionOption?.value || prev.sectionId,
      rollNo: parsed.rollNo || prev.rollNo,
      fatherName: parsed.fatherName || prev.fatherName,
      motherName: parsed.motherName || prev.motherName,
      fatherPhone1: parsed.fatherPhone || prev.fatherPhone1,
      motherPhone1: parsed.motherPhone || prev.motherPhone1,
      whatsappNumber: parsed.whatsappNumber || prev.whatsappNumber,
      fullAddress: parsed.address || prev.fullAddress,
      villageTown: parsed.villageTown || prev.villageTown,
      district: parsed.district || prev.district,
      schoolName: parsed.schoolName || prev.schoolName,
      schoolBoard: parsed.schoolBoard || prev.schoolBoard,
      previousClassBoard: parsed.previousClassBoard || prev.previousClassBoard,
      previousMarks: parsed.previousMarks || prev.previousMarks,
      previousPercentage: parsed.previousPercentage || prev.previousPercentage,
    }));
  };

  const autoCreateFromOCR = async (parsed: ParsedStudentData) => {
    const classOption = classes?.find((c: any) => c.label === parsed.className);
    const sectionOption = sections?.find((s: any) => s.name === parsed.sectionName);
    if (!classOption) return;

    const payload = {
      studentName: parsed.studentName || '',
      gender: parsed.gender || undefined,
      dateOfBirth: parsed.dateOfBirth || undefined,
      classId: parseInt(classOption.value),
      sectionId: sectionOption ? parseInt(sectionOption.value) : undefined,
      rollNo: parsed.rollNo || undefined,
      fatherName: parsed.fatherName || undefined,
      motherName: parsed.motherName || undefined,
      fatherPhone1: parsed.fatherPhone || undefined,
      motherPhone1: parsed.motherPhone || undefined,
      whatsappNumber: parsed.whatsappNumber || undefined,
      fullAddress: parsed.address || undefined,
      villageTown: parsed.villageTown || undefined,
      district: parsed.district || undefined,
      schoolName: parsed.schoolName || undefined,
      schoolBoard: parsed.schoolBoard || undefined,
      previousClassBoard: parsed.previousClassBoard || undefined,
      previousMarks: parsed.previousMarks || undefined,
      previousPercentage: parsed.previousPercentage ? parseFloat(parsed.previousPercentage) : undefined,
      parents: {
        fatherName: parsed.fatherName || undefined,
        motherName: parsed.motherName || undefined,
        fatherPhone1: parsed.fatherPhone || undefined,
        motherPhone1: parsed.motherPhone || undefined,
        whatsappNumber: parsed.whatsappNumber || undefined,
      },
      address: {
        villageTown: parsed.villageTown || undefined,
        district: parsed.district || undefined,
        fullAddress: parsed.address || undefined,
      },
    };

    saveMutation.mutate(payload);
  };

  const handleOCRUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsOCRLoading(true);
    toast.info('Processing OCR, please wait...');

    try {
      const ocrResult = await ocrService.uploadFile(file);
      if (!ocrResult.success || !ocrResult.text) {
        toast.error(ocrResult.error || 'OCR failed to extract text');
        return;
      }

      toast.success('OCR text extracted! Now parsing with AI...');

      const parsed = await aiService.parseStudentFromOCR(ocrResult.text);
      if (!parsed) {
        toast.error('AI failed to parse student data');
        return;
      }

      const classOption = classes?.find((c: any) => c.label === parsed.className);
      if (parsed.studentName && classOption) {
        // Enough data for auto-create — show preview
        setOcrPreview({ show: true, data: parsed, parsedText: ocrResult.text });
      } else {
        // Not enough data — just fill the form
        fillFormFromParsed(parsed);
        toast.success('Form auto-filled from admission document!');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to process document');
    } finally {
      setIsOCRLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (createAnother = false) => {
    if (!formData.studentName || !formData.classId) {
      toast.error('Student name and class are required');
      setStep(1);
      return;
    }

    const fatherContact = formData.contacts.find(c => c.label === 'Father');
    const motherContact = formData.contacts.find(c => c.label === 'Mother');

    const payload = {
      studentName: formData.studentName,
      gender: formData.gender || undefined,
      dateOfBirth: formData.dateOfBirth || undefined,
      classId: parseInt(formData.classId),
      sectionId: formData.batchId ? parseInt(formData.batchId) : undefined,
      batchId: formData.batchId ? parseInt(formData.batchId) : undefined,
      rollNo: formData.rollNo || undefined,
      admissionDate: formData.admissionDate,
      status: formData.status,
      schoolName: formData.schoolName || undefined,
      schoolBoard: formData.schoolBoard || undefined,
      previousClassBoard: formData.previousClassBoard || undefined,
      previousMarks: formData.previousMarks || undefined,
      previousPercentage: formData.previousPercentage ? parseFloat(formData.previousPercentage) : undefined,
      scholarshipApplicable: formData.scholarshipApplicable,
      parents: {
        fatherName: formData.fatherName || undefined,
        fatherQualification: formData.fatherQualification || undefined,
        fatherOccupation: formData.fatherOccupation || undefined,
        motherName: formData.motherName || undefined,
        motherQualification: formData.motherQualification || undefined,
        motherOccupation: formData.motherOccupation || undefined,
        fatherPhone1: fatherContact?.phone || undefined,
        fatherPhone2: fatherContact?.altPhone || undefined,
        motherPhone1: motherContact?.phone || undefined,
        motherPhone2: motherContact?.altPhone || undefined,
        whatsappNumber: formData.whatsappNumber || undefined,
        preferredMessagingNumber: formData.preferredMessagingNumber || undefined,
      },
      address: {
        houseNo: formData.houseNo || undefined,
        villageTown: formData.villageTown || undefined,
        postOffice: formData.postOffice || undefined,
        district: formData.district || undefined,
        fullAddress: formData.fullAddress || undefined,
      },
      siblings: formData.siblings.length > 0 ? formData.siblings : undefined,
    };

    saveMutation.mutate(payload, {
      onSuccess: () => {
        if (createAnother) {
          setFormData(emptyFormData);
          setStep(1);
          clearPhoto();
          setTimeout(() => nameInputRef.current?.focus(), 100);
          toast.success('Student created! Add another one below');
        }
      },
    });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSubmit(false);
        } else if (e.key === 's' && e.shiftKey) {
          e.preventDefault();
          handleSubmit(true);
        } else if (e.key === 'k') {
          e.preventDefault();
          setShowShortcuts(prev => !prev);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [formData, step]);

  if (isLoadingStudent) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-light-elevated dark:bg-dark-elevated animate-pulse" />
          <div className="space-y-2">
            <div className="w-48 h-6 rounded bg-light-elevated dark:bg-dark-elevated animate-pulse" />
            <div className="w-32 h-4 rounded bg-light-elevated dark:bg-dark-elevated animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="w-24 h-4 rounded bg-light-elevated dark:bg-dark-elevated animate-pulse" />
              <div className="w-full h-10 rounded-xl bg-light-elevated dark:bg-dark-elevated animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
    <TooltipProvider>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => {
              const profileModule = searchParams.get('module');
              const profileClassId = searchParams.get('classId');
              const profileBatchId = searchParams.get('batchId');
              if (isEdit && profileModule && profileClassId) {
                navigate(`/students/${editId}?module=${profileModule}&classId=${profileClassId}&batchId=${profileBatchId || ''}`);
              } else if (isModuleEdit && moduleParam && routeClassId) {
                navigate(`/modules/${moduleParam}/classes/${routeClassId}/batches/${routeBatchId || ''}`);
              } else {
                navigate(isEdit ? `/students/${editId}` : '/modules/students/classes');
              }
            }}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-base sm:text-2xl font-bold text-light-text dark:text-dark-text">
                {isEdit ? 'Edit Student' : 'Add New Student'}
              </h1>
              <p className="text-xs sm:text-sm text-light-text-muted mt-1">
                {isEdit ? 'Update student details' : 'Fill details to register new student'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowShortcuts(!showShortcuts)} className="text-light-text-muted">
              <Keyboard className="w-4 h-4 mr-1" />
              Shortcuts
            </Button>
          </div>
        </div>

        {showShortcuts && (
          <div className="mb-4 p-4 rounded-xl bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 text-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-purple-700 dark:text-purple-300">Keyboard Shortcuts</span>
              <Button variant="ghost" size="sm" onClick={() => setShowShortcuts(false)}>
                <CircleX className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-light-text-muted">
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 rounded bg-light-elevated dark:bg-dark-elevated border border-light-border dark:border-dark-border text-xs font-mono">Ctrl+Enter</kbd>
                <span>Save</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 rounded bg-light-elevated dark:bg-dark-elevated border border-light-border dark:border-dark-border text-xs font-mono">Ctrl+Shift+S</kbd>
                <span>Save & Add Another</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 rounded bg-light-elevated dark:bg-dark-elevated border border-light-border dark:border-dark-border text-xs font-mono">Ctrl+K</kbd>
                <span>Toggle Shortcuts</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 rounded bg-light-elevated dark:bg-dark-elevated border border-light-border dark:border-dark-border text-xs font-mono">Tab</kbd>
                <span>Next Field</span>
              </div>
            </div>
          </div>
        )}

        {/* OCR Preview / Auto-create */}
        {ocrPreview.show && ocrPreview.data && (
          <div className="mb-4 p-4 sm:p-5 rounded-xl bg-lime-50 dark:bg-lime-500/10 border border-lime-200 dark:border-lime-500/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ScanLine className="w-5 h-5 text-lime-600" />
                <span className="font-semibold text-sm sm:text-base text-lime-700 dark:text-lime-300">Document Scanned Successfully</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setOcrPreview({ show: false, data: null, parsedText: '' })}>
                <CircleX className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs sm:text-sm text-light-text-muted mb-3">AI extracted the following details. Create student directly or review in form.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {[
                ['Name', ocrPreview.data.studentName],
                ['Class', ocrPreview.data.className],
                ['Section', ocrPreview.data.sectionName],
                ['Gender', ocrPreview.data.gender],
                ['DOB', ocrPreview.data.dateOfBirth],
                ['Roll No', ocrPreview.data.rollNo],
                ['Father', ocrPreview.data.fatherName],
                ['Mother', ocrPreview.data.motherName],
                ['Phone', ocrPreview.data.fatherPhone || ocrPreview.data.motherPhone],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label as string} className="text-xs sm:text-sm">
                  <span className="text-light-text-muted">{label}: </span>
                  <span className="font-medium text-light-text dark:text-dark-text">{value as string}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => { autoCreateFromOCR(ocrPreview.data); setOcrPreview({ show: false, data: null, parsedText: '' }); }}
                disabled={saveMutation.isPending}
                className="gap-2"
              >
                {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                Create Student Directly
              </Button>
              <Button
                variant="outline"
                onClick={() => { fillFormFromParsed(ocrPreview.data); setOcrPreview({ show: false, data: null, parsedText: '' }); setStep(1); }}
              >
                Fill & Review Form
              </Button>
            </div>
          </div>
        )}

        {/* Step Tabs */}
        <div className="flex items-center gap-2 mb-4 sm:mb-6">
          <button
            onClick={() => setStep(1)}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-3 rounded-xl text-xs sm:text-sm font-medium transition-all min-h-[44px] ${
              step === 1
                ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
                : 'bg-light-elevated dark:bg-dark-elevated text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-border dark:hover:bg-dark-border/50'
            }`}
          >
            <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Basic Details</span>
            <span className="sm:hidden">Basic</span>
          </button>
          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-light-text-muted" />
          <button
            onClick={() => setStep(2)}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-3 rounded-xl text-xs sm:text-sm font-medium transition-all min-h-[44px] ${
              step === 2
                ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
                : 'bg-light-elevated dark:bg-dark-elevated text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-border dark:hover:bg-dark-border/50'
            }`}
          >
            <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Family & Address</span>
            <span className="sm:hidden">Family</span>
          </button>
        </div>

        {/* Form */}
        <div className="glass-card p-3 sm:p-6 rounded-2xl">
          {step === 1 ? (
            <div className="space-y-4 sm:space-y-6">
              {/* Photo Upload */}
              <div className="flex flex-col items-center gap-3 mb-2">
                <div className="relative">
                  <div className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 rounded-full bg-purple-100 dark:bg-purple-500/10 flex items-center justify-center overflow-hidden border-2 border-purple-200 dark:border-purple-500/20">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Student" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-purple-500">
                        {studentInitial || <User className="w-10 h-10 sm:w-12 sm:h-12 text-purple-400" />}
                      </span>
                    )}
                  </div>
                  {photoPreview && (
                    <button
                      type="button"
                      onClick={clearPhoto}
                      className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                      aria-label="Remove photo"
                    >
                      <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    </button>
                  )}
                </div>

                {/* Hidden inputs */}
                <input
                  type="file"
                  ref={cameraInputRef}
                  onChange={handlePhotoSelect}
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                />
                <input
                  type="file"
                  ref={photoInputRef}
                  onChange={handlePhotoSelect}
                  accept="image/*"
                  className="hidden"
                />

                <div className="flex items-center gap-2 flex-wrap justify-center">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => cameraInputRef.current?.click()}
                    className="gap-1.5 text-xs sm:text-sm"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    <span className="hidden sm:inline">Take Photo</span>
                  </Button>
                  <span className="text-xs text-light-text-muted">or</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => photoInputRef.current?.click()}
                    className="gap-1.5 text-xs sm:text-sm"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Upload</span>
                  </Button>
                  {photoFile && isEdit && editId && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={async () => {
                        if (!photoFile || !editId) return;
                        setSavingPhoto(true);
                        const fd = new FormData();
                        fd.append('file', photoFile);
                        fd.append('documentType', 'student_photo');
                        fd.append('studentId', String(editId));
                        try {
                          const res: any = await api.upload('/api/documents/upload', fd);
                          toast.success('Photo saved');
                          setPhotoFile(null);
                          setSavingPhoto(true);
                          if (res.data?.public_url) {
                            const fullUrl = res.data.public_url.startsWith('http') ? res.data.public_url : `${API_URL}${res.data.public_url}`;
                            setPhotoPreview(fullUrl);
                          }
                          queryClient.invalidateQueries({ queryKey: ['student-edit', editId] });
                          queryClient.invalidateQueries({ queryKey: ['student', editId] });
                          queryClient.invalidateQueries({ queryKey: ['module-student-detail', editId] });
                          setTimeout(() => setSavingPhoto(false), 1500);
                        } catch (err: any) {
                          toast.error(err.message || 'Failed to save photo');
                          setSavingPhoto(false);
                        }
                      }}
                      className="gap-1.5 text-xs sm:text-sm"
                      disabled={savingPhoto}
                    >
                      {savingPhoto ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving</>
                      ) : (
                        <><Save className="w-3.5 h-3.5" /> Save Photo</>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Student Info Section */}
              <div className="flex items-center gap-2 mb-3 sm:mb-4 pb-2 border-b border-light-border dark:border-dark-border">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                <h2 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text">Student Information</h2>
                <div className="flex-1" />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleOCRUpload}
                  accept="image/*,.pdf,.doc,.docx"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isOCRLoading}
                  className="h-8 sm:h-9 gap-1.5 text-xs sm:text-sm"
                >
                  {isOCRLoading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <ScanLine className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden sm:inline">{isOCRLoading ? 'Processing...' : 'Auto-fill from Photo'}</span>
                  <span className="sm:hidden">{isOCRLoading ? '...' : 'Scan'}</span>
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <FieldLabel label="Student Name" required tooltip={fieldTooltips.studentName} />
                  <IconInput
                    icon={User}
                    ref={nameInputRef}
                    value={formData.studentName}
                    onChange={(e) => updateField('studentName', e.target.value)}
                    placeholder="e.g., Rahul Kumar Sharma"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Gender" tooltip={fieldTooltips.gender} />
                  <IconSelect
                    key={`gender-${formData.gender}-${isLoadingStudent}`}
                    icon={UserCircle}
                    value={formData.gender}
                    onValueChange={(v) => updateField('gender', v)}
                    placeholder="Select Gender"
                  >
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </IconSelect>
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Date of Birth" tooltip={fieldTooltips.dateOfBirth} />
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => updateField('dateOfBirth', e.target.value)}
                    className="w-full h-11 rounded-xl border border-light-border dark:border-dark-border bg-light-elevated dark:bg-dark-elevated px-3 text-sm text-light-text dark:text-dark-text font-normal focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 mobile-input"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Admission Date" required tooltip={fieldTooltips.admissionDate} />
                  <input
                    type="date"
                    value={formData.admissionDate}
                    onChange={(e) => updateField('admissionDate', e.target.value)}
                    className="w-full h-11 rounded-xl border border-light-border dark:border-dark-border bg-light-elevated dark:bg-dark-elevated px-3 text-sm text-light-text dark:text-dark-text font-normal focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 mobile-input"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Class" required tooltip={fieldTooltips.classId} />
                  <IconSelect
                    key={`class-${formData.classId}-${classesLoading}`}
                    icon={GraduationCap}
                    value={formData.classId}
                    onValueChange={(v) => updateField('classId', v)}
                    placeholder="Select Class"
                  >
                    {classesLoading ? (
                      <SelectItem value="loading" disabled>Loading...</SelectItem>
                    ) : (
                      classes?.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))
                    )}
                  </IconSelect>
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Batch" tooltip={fieldTooltips.sectionId} />
                  <IconSelect
                    key={`batch-${formData.classId}-${formData.batchId}`}
                    icon={Building2}
                    value={formData.batchId}
                    onValueChange={(v) => { updateField('batchId', v); updateField('sectionId', v); }}
                    placeholder="Select Batch"
                  >
                    {batchesLoading ? (
                      <SelectItem value="loading2" disabled>Loading...</SelectItem>
                    ) : (
                      (batches || []).filter((b: any) => !formData.classId || String(b.class_id) === formData.classId || !b.class_id).map((opt: any) => (
                        <SelectItem key={String(opt.id)} value={String(opt.id)}>{opt.name}</SelectItem>
                      ))
                    )}
                  </IconSelect>
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Roll No" tooltip={fieldTooltips.rollNo} />
                  <IconInput
                    icon={Hash}
                    value={formData.rollNo}
                    onChange={(e) => updateField('rollNo', e.target.value)}
                    placeholder="e.g., 101"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="School Name" tooltip={fieldTooltips.schoolName} />
                  <IconInput
                    icon={School}
                    value={formData.schoolName}
                    onChange={(e) => updateField('schoolName', e.target.value)}
                    placeholder="e.g., DAV Public School"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="School Board" tooltip={fieldTooltips.schoolBoard} />
                  <IconInput
                    icon={Landmark}
                    value={formData.schoolBoard}
                    onChange={(e) => updateField('schoolBoard', e.target.value)}
                    placeholder="e.g., CBSE, ICSE, State"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Previous Percentage" tooltip={fieldTooltips.previousPercentage} />
                  <IconInput
                    icon={Percent}
                    type="number"
                    value={formData.previousPercentage}
                    onChange={(e) => updateField('previousPercentage', e.target.value)}
                    placeholder="e.g., 85.5"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Status Section */}
              <div className="space-y-3 pt-4">
                <FieldLabel label="Student Status" tooltip={fieldTooltips.status} />
                <div className="flex flex-wrap gap-3">
                  {statusOptions.map((status) => {
                    const Icon = status.icon;
                    const isSelected = formData.status === status.value;
                    return (
                      <button
                        key={status.value}
                        onClick={() => updateField('status', status.value)}
                        className={`flex items-center gap-2.5 px-5 py-3 rounded-xl border-2 transition-all duration-200 ${
                          isSelected
                            ? `${status.borderColor} ${status.bgColor} shadow-lg shadow-${status.borderColor}/20`
                            : 'border-transparent bg-light-elevated dark:bg-dark-elevated hover:bg-light-border dark:hover:bg-dark-border/50'
                        }`}
                        type="button"
                      >
                        <div className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all ${
                          isSelected ? status.color : 'bg-gray-300 dark:bg-gray-600'
                        }`}>
                          <Icon className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
                        </div>
                        <div className="flex flex-col items-start">
                          <span className={`font-semibold text-sm ${isSelected ? status.textColor : 'text-light-text dark:text-dark-text'}`}>
                            {status.label}
                          </span>
                          <span className="text-xs text-light-text-muted">
                            {status.value === 'active' ? 'Currently enrolled' : status.value === 'drop' ? 'Dropped out' : 'Awaiting admission'}
                          </span>
                        </div>
                        {isSelected && (
                          <CircleCheck className="w-5 h-5 text-lime-500 ml-2" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Scholarship Section */}
              <div className="space-y-3 pt-2">
                <FieldLabel label="Scholarship" tooltip={fieldTooltips.scholarship} />
                <button
                  onClick={() => updateField('scholarshipApplicable', !formData.scholarshipApplicable)}
                  className={`flex items-center gap-3 px-5 py-3 rounded-xl border-2 transition-all duration-200 ${
                    formData.scholarshipApplicable
                      ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-500/10 shadow-lg shadow-yellow-500/20'
                      : 'border-transparent bg-light-elevated dark:bg-dark-elevated hover:bg-light-border dark:hover:bg-dark-border/50'
                  }`}
                  type="button"
                >
                  <div className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all ${
                    formData.scholarshipApplicable ? 'bg-yellow-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}>
                    <Trophy className={`w-4 h-4 ${formData.scholarshipApplicable ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
                  </div>
                  <div className="flex flex-col items-start">
                    <span className={`font-semibold text-sm ${formData.scholarshipApplicable ? 'text-yellow-600' : 'text-light-text dark:text-dark-text'}`}>
                      Scholarship Applicable
                    </span>
                    <span className="text-xs text-light-text-muted">
                      {formData.scholarshipApplicable ? 'Eligible for fee waiver' : 'Click to mark eligible'}
                    </span>
                  </div>
                  {formData.scholarshipApplicable && (
                    <CircleCheck className="w-5 h-5 text-yellow-500 ml-2" />
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Family Section */}
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-light-border dark:border-dark-border">
                <Users className="w-5 h-5 text-electric-500" />
                <h2 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text">Family Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <FieldLabel label="Father Name" tooltip={fieldTooltips.fatherName} />
                  <IconInput
                    icon={User}
                    value={formData.fatherName}
                    onChange={(e) => updateField('fatherName', e.target.value)}
                    placeholder="e.g., Rajesh Kumar Sharma"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Father Occupation" tooltip={fieldTooltips.fatherOccupation} />
                  <IconInput
                    icon={Briefcase}
                    value={formData.fatherOccupation}
                    onChange={(e) => updateField('fatherOccupation', e.target.value)}
                    placeholder="e.g., Engineer, Teacher, Business"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Mother Name" tooltip={fieldTooltips.motherName} />
                  <IconInput
                    icon={User}
                    value={formData.motherName}
                    onChange={(e) => updateField('motherName', e.target.value)}
                    placeholder="e.g., Priya Sharma"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Mother Occupation" tooltip={fieldTooltips.motherOccupation} />
                  <IconInput
                    icon={Briefcase}
                    value={formData.motherOccupation}
                    onChange={(e) => updateField('motherOccupation', e.target.value)}
                    placeholder="e.g., Homemaker, Doctor, Teacher"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="WhatsApp Number" tooltip={fieldTooltips.whatsappNumber} />
                  <IconInput
                    icon={Phone}
                    type="tel"
                    value={formData.whatsappNumber}
                    onChange={(e) => updateField('whatsappNumber', e.target.value)}
                    placeholder="e.g., +91 98765 43210"
                  />
                </div>
              </div>

              {/* Phone Contacts Section - separate from family grid */}
              <div className="space-y-4 mt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-purple-500" />
                    <h2 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text">Phone Contacts</h2>
                    {formData.contacts.length > 0 && (
                      <span className="text-xs text-light-text-muted">({formData.contacts.length})</span>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={addContact} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.contacts.map((contact, index) => (
                    <div key={index} className="rounded-xl bg-light-elevated dark:bg-dark-elevated border border-light-border dark:border-dark-border p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold text-[10px]">{index + 1}</div>
                          <Select value={contact.label} onValueChange={(v) => updateContact(index, 'label', v)}>
                            <SelectTrigger className="h-8 text-xs w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Father">Father</SelectItem>
                              <SelectItem value="Mother">Mother</SelectItem>
                              <SelectItem value="Self">Self</SelectItem>
                              <SelectItem value="Guardian">Guardian</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteConfirm({ type: 'contact', index })} className="h-6 w-6 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 shrink-0">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="tel"
                          value={contact.phone}
                          onChange={(e) => updateContact(index, 'phone', e.target.value)}
                          placeholder="Phone number"
                          className="h-9 rounded-lg border border-light-border dark:border-dark-border bg-white dark:bg-dark-elevated text-xs px-2.5 text-light-text dark:text-dark-text"
                        />
                        <input
                          type="tel"
                          value={contact.altPhone}
                          onChange={(e) => updateContact(index, 'altPhone', e.target.value)}
                          placeholder="Alt phone"
                          className="h-9 rounded-lg border border-light-border dark:border-dark-border bg-white dark:bg-dark-elevated text-xs px-2.5 text-light-text dark:text-dark-text"
                        />
                      </div>
                    </div>
                  ))}
                  {formData.contacts.length === 0 && (
                    <div className="text-center py-4 border-2 border-dashed border-light-border dark:border-dark-border rounded-xl">
                      <Phone className="w-6 h-6 text-light-text-muted mx-auto mb-1" />
                      <p className="text-xs text-light-text-muted">No contacts added</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Address Section */}
              <div className="flex items-center gap-2 mt-6 mb-4 pb-2 border-b border-light-border dark:border-dark-border">
                <MapPin className="w-5 h-5 text-tangerine-500" />
                <h2 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text">Address Details</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <FieldLabel label="House No" tooltip={fieldTooltips.houseNo} />
                  <IconInput
                    icon={Home}
                    value={formData.houseNo}
                    onChange={(e) => updateField('houseNo', e.target.value)}
                    placeholder="e.g., 123, Block B"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Village/Town" tooltip={fieldTooltips.villageTown} />
                  <IconInput
                    icon={MapPin}
                    value={formData.villageTown}
                    onChange={(e) => updateField('villageTown', e.target.value)}
                    placeholder="e.g., Green Park"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="Post Office" tooltip={fieldTooltips.postOffice} />
                  <IconInput
                    icon={Mail}
                    value={formData.postOffice}
                    onChange={(e) => updateField('postOffice', e.target.value)}
                    placeholder="e.g., City Post Office"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel label="District" tooltip={fieldTooltips.district} />
                  <IconInput
                    icon={MapPinHouse}
                    value={formData.district}
                    onChange={(e) => updateField('district', e.target.value)}
                    placeholder="e.g., Delhi, Mumbai, Bangalore"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <FieldLabel label="Full Address" tooltip={fieldTooltips.fullAddress} />
                  <IconTextarea
                    icon={FileText}
                    value={formData.fullAddress}
                    onChange={(e) => updateField('fullAddress', e.target.value)}
                    placeholder="e.g., 123 Block B, Green Park, City Post Office, Delhi - 110001"
                    className="min-h-[80px]"
                  />
                </div>
              </div>

              {/* Siblings Section */}
              <div className="space-y-4 mt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-lime-500" />
                    <h2 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text">Siblings</h2>
                    {formData.siblings.length > 0 && (
                      <span className="text-xs text-light-text-muted">({formData.siblings.length})</span>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={addSibling} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add Sibling
                  </Button>
                </div>
                <div className="space-y-3">
                  {formData.siblings.map((sibling, index) => (
                    <div key={index} className="rounded-xl bg-light-elevated dark:bg-dark-elevated border border-light-border dark:border-dark-border p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-lime-100 dark:bg-lime-500/20 flex items-center justify-center text-lime-600 dark:text-lime-400 font-bold text-xs">{index + 1}</div>
                          <span className="text-xs font-medium text-light-text dark:text-dark-text">Sibling {index + 1}</span>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteConfirm({ type: 'sibling', index })} className="h-7 w-7 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <IconInput
                          icon={User}
                          value={sibling.name}
                          onChange={(e) => updateSibling(index, 'name', e.target.value)}
                          placeholder="Name"
                        />
                        <IconInput
                          icon={GraduationCap}
                          value={sibling.className}
                          onChange={(e) => updateSibling(index, 'className', e.target.value)}
                          placeholder="Class"
                        />
                        <IconInput
                          icon={School}
                          value={sibling.school}
                          onChange={(e) => updateSibling(index, 'school', e.target.value)}
                          placeholder="School"
                        />
                      </div>
                    </div>
                  ))}
                  {formData.siblings.length === 0 && (
                    <div className="text-center py-6 border-2 border-dashed border-light-border dark:border-dark-border rounded-xl">
                      <Users className="w-8 h-8 text-light-text-muted mx-auto mb-2" />
                      <p className="text-sm text-light-text-muted">No siblings added. Click "Add Sibling" to add.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between mt-6 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button variant="outline" size="sm" onClick={() => setStep(step - 1)} className="gap-1.5 text-xs">
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </Button>
            )}
            {isEdit && (
              <Button variant="ghost" size="sm" onClick={() => {
                const profileModule = searchParams.get('module');
                const profileClassId = searchParams.get('classId');
                const profileBatchId = searchParams.get('batchId');
                if (profileModule && profileClassId) {
                  navigate(`/students/${editId}?module=${profileModule}&classId=${profileClassId}&batchId=${profileBatchId || ''}`);
                } else if (isModuleEdit && moduleParam && routeClassId) {
                  navigate(`/modules/${moduleParam}/classes/${routeClassId}/batches/${routeBatchId || formData.batchId}`);
                } else {
                  navigate(-1);
                }
              }} className="gap-1.5 text-xs text-light-text-muted">
                <CircleX className="w-3.5 h-3.5" /> Cancel
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {step < 2 ? (
              <Button size="sm" onClick={() => setStep(step + 1)} className="gap-1.5 text-xs">
                Continue <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  onClick={() => handleSubmit(false)}
                  disabled={saveMutation.isPending || (isEdit && !isDirty && !photoFile)}
                  className="gap-1.5 text-xs"
                >
                  {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {isEdit ? 'Update Student' : isEnquiry ? 'Add Enquiry' : 'Create Student'}
                </Button>
                {!isEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSubmit(true)}
                    disabled={saveMutation.isPending}
                    className="gap-1.5 text-xs"
                  >
                    <Printer className="w-3.5 h-3.5" /> Save & Add Another
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>

    <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
          <AlertDialogDescription>
            {deleteConfirm?.type === 'sibling'
              ? 'Are you sure you want to remove this sibling? This action cannot be undone.'
              : 'Are you sure you want to remove this contact? This action cannot be undone.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-rose-500 hover:bg-rose-600 text-white"
            onClick={() => {
              if (deleteConfirm?.type === 'sibling') {
                removeSibling(deleteConfirm.index);
              } else if (deleteConfirm?.type === 'contact') {
                removeContact(deleteConfirm.index);
              }
              setDeleteConfirm(null);
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
